import Icon from '@components/common/Icon';
import { DATA_BASE_URL } from '@lib/constants';

const mathAchievements = [
  {
    id: 1,
    title: 'CIMC Mathematics Award',
    description: 'Recognized for outstanding achievement in mathematics through the prestigious CIMC award program, demonstrating exceptional problem-solving abilities and mathematical reasoning.',
    icon: 'research',
    year: '2024',
    certificate: `${DATA_BASE_URL}/data/awards/certificates/cibc_and_hypatia_maths.pdf`
  },
  {
    id: 2,
    title: 'Hypatia Mathematics Award',
    description: 'Honored with the Hypatia Mathematics Award for excellence in advanced mathematical studies, named after the renowned ancient mathematician Hypatia of Alexandria.',
    icon: 'research',
    year: '2024',
    certificate: `${DATA_BASE_URL}/data/awards/certificates/cibc_and_hypatia_maths.pdf`
  },
  {
    id: 3,
    title: 'Mathematics Department Award',
    description: 'Departmental recognition for exceptional performance, dedication, and leadership in mathematics coursework throughout the academic year.',
    icon: 'academics',
    year: '2023',
    image: `${DATA_BASE_URL}/data/awards/photos/mathematics_department_award_2023.webp`
  },
  {
    id: 4,
    title: 'Mathematics Honors Society',
    description: 'Inducted into the Mathematics Honors Society for maintaining outstanding grades in advanced mathematics courses and demonstrating a passion for mathematical exploration.',
    icon: 'academics',
    year: '2025',
    image: `${DATA_BASE_URL}/data/awards/photos/mathematics_honors_society_2025.webp`
  }
];

const MathematicsSpecializationSection = () => {
  return (
    <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8">
      <div className="container mx-auto max-w-7xl">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16">
          <div className="flex justify-center mb-6">
            <div
              className="inline-flex items-center justify-center w-16 h-16 rounded-xl shadow-card"
              style={{
                backgroundColor: 'var(--color-accent-6)',
                color: 'var(--color-button-primary-text)'
              }}
            >
              <Icon name="research" size="xl" />
            </div>
          </div>
          <h2
            className="text-4xl sm:text-5xl font-heading font-bold mb-4"
            style={{ color: 'var(--color-heading)' }}
          >
            Mathematics Specialization
          </h2>
          <p
            className="text-lg sm:text-xl max-w-3xl mx-auto"
            style={{ color: 'var(--color-subtle)' }}
          >
            Distinguished recognition in mathematics with multiple prestigious awards and honors
          </p>
        </div>

        {/* Mathematics Quote */}
        <div
          className="max-w-4xl mx-auto mb-12 p-8 rounded-xl shadow-card"
          style={{
            backgroundColor: 'var(--color-card-bg)',
            borderLeftColor: 'var(--color-accent-6)',
            borderLeftWidth: '4px',
            borderLeftStyle: 'solid'
          }}
        >
          <p
            className="text-lg italic mb-2"
            style={{ color: 'var(--color-subheading)' }}
          >
            "Mathematics is not about numbers, equations, computations, or algorithms: it is about understanding."
          </p>
          <p
            className="text-sm font-semibold"
            style={{ color: 'var(--color-muted)' }}
          >
            - William Paul Thurston
          </p>
        </div>

        {/* Mathematics Achievements Grid - Horizontal scroll on mobile, grid on desktop */}
        <div className="md:grid md:grid-cols-2 md:gap-6 lg:gap-8 overflow-x-auto md:overflow-x-visible">
          <div className="flex md:contents gap-6 pb-4 md:pb-0">
          {mathAchievements.map((achievement) => (
            <div
              key={achievement.id}
              className="group rounded-xl p-6 shadow-card transition-all duration-300 hover:shadow-card-hover flex-shrink-0 w-[320px] md:w-auto"
              style={{
                backgroundColor: 'var(--color-card-bg)',
                borderColor: 'var(--color-card-border)',
                borderWidth: '1px',
                borderStyle: 'solid'
              }}
            >
              {/* Icon and Year Header */}
              <div className="flex items-start justify-between mb-4">
                <div
                  className="flex items-center justify-center w-12 h-12 rounded-lg"
                  style={{
                    backgroundColor: 'var(--color-accent-1)',
                    color: 'var(--color-accent-6)'
                  }}
                >
                  <Icon name={achievement.icon} size="lg" />
                </div>
                <span
                  className="px-3 py-1 rounded-full text-sm font-bold"
                  style={{
                    backgroundColor: 'var(--color-accent-6)',
                    color: 'var(--color-button-primary-text)'
                  }}
                >
                  {achievement.year}
                </span>
              </div>

              {/* Achievement Details */}
              <h3
                className="text-2xl font-bold mb-3"
                style={{ color: 'var(--color-heading)' }}
              >
                {achievement.title}
              </h3>
              <p
                className="text-base leading-relaxed mb-4"
                style={{ color: 'var(--color-subtle)' }}
              >
                {achievement.description}
              </p>

              {/* View Link */}
              {achievement.certificate && (
                <a
                  href={achievement.certificate}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-semibold transition-colors duration-200"
                  style={{ color: 'var(--color-accent-6)' }}
                >
                  View Certificate
                  <Icon name="arrow" size="sm" />
                </a>
              )}
              {achievement.image && (
                <a
                  href={achievement.image}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-semibold transition-colors duration-200"
                  style={{ color: 'var(--color-accent-6)' }}
                >
                  View Award
                  <Icon name="arrow" size="sm" />
                </a>
              )}
            </div>
          ))}
          </div>
        </div>

        {/* Mathematics Application Note */}
        <div className="mt-12 max-w-4xl mx-auto">
          <div
            className="rounded-xl p-6 sm:p-8"
            style={{
              backgroundColor: 'var(--color-accent-1)',
              borderColor: 'var(--color-accent-5)',
              borderWidth: '1px',
              borderStyle: 'solid'
            }}
          >
            <div className="flex items-start gap-4">
              <Icon
                name="research"
                size="xl"
                className="flex-shrink-0 mt-1"
                style={{ color: 'var(--color-accent-6)' }}
              />
              <div>
                <h4
                  className="text-xl font-bold mb-2"
                  style={{ color: 'var(--color-heading)' }}
                >
                  Applied Mathematics in Chess
                </h4>
                <p
                  className="text-base leading-relaxed"
                  style={{ color: 'var(--color-subtle)' }}
                >
                  Beyond formal mathematics coursework, I apply mathematical thinking in competitive chess,
                  utilizing probability theory, pattern recognition, and strategic calculation to achieve a
                  National Master rating. This demonstrates the practical application of mathematical reasoning
                  in complex, real-world problem-solving scenarios.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MathematicsSpecializationSection;
